import React, { type ReactNode, useCallback, useMemo, useState } from 'react';

import { type Event } from '@clubspark-node/clubspark-tennis-types';
import { Grid, IconButton, Link } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import HelpIcon from 'assets/icons/icn _ 24px _ help.svg?react';
import { getEnvConfig } from 'config/environment';
import { useGenderDisplayName } from 'hooks/event/useGenderDisplayName';
import { useRankingsAndRatingsContext } from 'hooks/rankingsAndRatings/useRankingsAndRatingsContext';
import useScalings from 'hooks/scalings/useScalings';
import { factorySafeTimeToDayjs } from 'utils/dateTimeUtils';
import { type ScalingType } from 'utils/scalings';

import RankingRatingLinkButton from './rankingsAndRatings/RankingRatingLinkButton';
import RankingsDialog from './rankingsAndRatings/RankingsDialog';
import RatingsDialog from './rankingsAndRatings/RatingsDialog';
import { RatingTypeEnum } from './rankingsAndRatings/types';
import { useStyles } from './styles';

export const Pipe: React.FC<React.PropsWithChildren<unknown>> = React.memo(() => {
  const { classes } = useStyles();
  return <div className={classes.pipe} />;
});

interface RankingsAndRatingsComponentProps {
  rankingTimeItem: any;
  ratingTimeItem: any;
  wtnTimeItem: any;
  mensRankingTimeItem: any;
  womensRankingTimeItem: any;
  ptTimeItem: any;
  event: Event;
  scalesToHide?: ScalingType[];
  localTimeZone?: string;
}

// TODO: COMP-8470 - Rework this
const RankingsAndRatingsComponent: React.FC<React.PropsWithChildren<RankingsAndRatingsComponentProps>> = React.memo(
  ({
    rankingTimeItem,
    ratingTimeItem,
    wtnTimeItem,
    mensRankingTimeItem,
    womensRankingTimeItem,
    ptTimeItem,
    event,
    scalesToHide = [],
    localTimeZone,
  }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const { rankingsConfig } = useRankingsAndRatingsContext();
    const [rankingsOpen, setRankingsOpen] = useState<boolean>(false);
    const [ratingsOpen, setRatingsOpen] = useState<boolean>(false);
    const [isRankings, setIsRankings] = useState<boolean>(false);
    const [ratingType, setRatingType] = useState<RatingTypeEnum>(null);

    const handleRankingsDialogOpen = useCallback((isRankings: boolean) => {
      setIsRankings(isRankings);
      setRankingsOpen(true);
    }, []);

    const handleRatingsDialogOpen = useCallback((isRankings: boolean, type?: RatingTypeEnum) => {
      setRatingType(type);
      setIsRankings(isRankings);
      setRatingsOpen(true);
    }, []);

    const handleRankingsDialogClose = useCallback(() => {
      setRankingsOpen(false);
    }, []);
    const handleRatingsDialogClose = useCallback(() => {
      setRatingsOpen(false);
    }, []);

    const emptyMessage = t('notPublished');
    const defaultDateTimeFormat = 'MMM D, YYYY h:mm A';

    const rankingAccessDate = rankingTimeItem && dayjs(rankingTimeItem.createdAt).format(defaultDateTimeFormat);
    const mensRankingAccessDate =
      mensRankingTimeItem && dayjs(mensRankingTimeItem.createdAt).format(defaultDateTimeFormat);
    const womensRankingAccessDate =
      womensRankingTimeItem && dayjs(womensRankingTimeItem.createdAt).format(defaultDateTimeFormat);
    const ratingAccessDate = ratingTimeItem && dayjs(ratingTimeItem.createdAt).format(defaultDateTimeFormat);
    const wtnAccessDate =
      wtnTimeItem && factorySafeTimeToDayjs(wtnTimeItem.itemValue, localTimeZone).format(defaultDateTimeFormat);
    const ptAccessDate =
      ptTimeItem && factorySafeTimeToDayjs(ptTimeItem.itemValue, localTimeZone).format(defaultDateTimeFormat);
    const rankingPubDate =
      rankingTimeItem &&
      factorySafeTimeToDayjs(rankingTimeItem.itemValue, localTimeZone)?.format(defaultDateTimeFormat);
    const ratingPubDate =
      ratingTimeItem && factorySafeTimeToDayjs(ratingTimeItem.itemValue, localTimeZone)?.format(defaultDateTimeFormat);
    const ptPubDate =
      ptTimeItem && factorySafeTimeToDayjs(ptTimeItem.itemValue, localTimeZone)?.format(defaultDateTimeFormat);
    const mensRankingPubDate =
      mensRankingTimeItem &&
      factorySafeTimeToDayjs(mensRankingTimeItem.itemValue, localTimeZone)?.format(defaultDateTimeFormat);
    const womensRankingPubDate =
      womensRankingTimeItem &&
      factorySafeTimeToDayjs(womensRankingTimeItem.itemValue, localTimeZone)?.format(defaultDateTimeFormat);
    const rankingMessage = (rankingPubDate && t('publishedDate', { date: rankingPubDate })) || emptyMessage;
    const ratingMessage = (ratingPubDate && t('publishedDate', { date: ratingPubDate })) || emptyMessage;
    const ptMessage = (ptPubDate && t('publishedDate', { date: ptPubDate })) || emptyMessage;
    // We have no single 'published' date for WTN, it's per ranking. We use 'access date' here
    const wtnMessage = (wtnAccessDate && t('updatedDate', { date: wtnAccessDate })) || emptyMessage;
    const { male: mensPrefix, female: womensPrefix } = useGenderDisplayName(event?.category?.subType);
    const mensRankingMessage = (mensRankingPubDate && t('publishedDate', { date: mensRankingPubDate })) || emptyMessage;
    const womensRankingMessage =
      (womensRankingPubDate && t('publishedDate', { date: womensRankingPubDate })) || emptyMessage;
    const showDoubleRankingsList =
      rankingsConfig?.isMixed && !rankingsConfig?.isFamily && !rankingsConfig?.isWheelchair;
    const { ageCategoryCode } = useMemo(() => event?.category || {}, [event?.category]);
    const age = ageCategoryCode?.split('U')[1];
    const rankingsDisabled = Number(age) < 12 || rankingsConfig?.isNtrpCombo;
    const { availableScalingTypes } = useScalings({ event });
    const displayedScalingTypes = useMemo(
      () =>
        availableScalingTypes
          .map(([scalingType]) => scalingType)
          .filter((scalingType) => !scalesToHide.includes(scalingType)),
      [availableScalingTypes, scalesToHide],
    );

    const ratingsDialogPubDate =
      ratingType === RatingTypeEnum.NTRP
        ? ratingPubDate
        : ratingType === RatingTypeEnum.WTN
          ? wtnAccessDate
          : ptPubDate;
    const ratingsDialogAccessDate =
      ratingType === RatingTypeEnum.NTRP
        ? ratingAccessDate
        : ratingType === RatingTypeEnum.WTN
          ? wtnAccessDate
          : ptAccessDate;

    const components: ReactNode[] = [
      ...(displayedScalingTypes.includes('Ranking')
        ? [
            <RankingRatingLinkButton
              key="Ranking"
              label={'Ranking'}
              onClick={() => (rankingsDisabled ? undefined : handleRankingsDialogOpen(true))}
            >
              {!rankingsConfig?.isMixed && (rankingsDisabled ? t('rankingIsNotAvailable') : rankingMessage)}
              {rankingsConfig?.isMixed &&
                rankingsConfig?.isFamily &&
                (rankingsDisabled ? t('rankingIsNotAvailable') : rankingMessage)}
              {rankingsConfig?.isMixed &&
                !rankingsConfig.isFamily &&
                (rankingsDisabled ? t('rankingIsNotAvailable') : `${mensPrefix}: ` + mensRankingMessage + ', ')}
              <br />
              {rankingsConfig?.isMixed &&
                !rankingsConfig.isFamily &&
                (!rankingsDisabled ? `${womensPrefix}: ` + womensRankingMessage : '')}
            </RankingRatingLinkButton>,
          ]
        : []),
      ...(displayedScalingTypes.includes('NTRP')
        ? [
            <RankingRatingLinkButton
              key="NTRP"
              label={'NTRP'}
              onClick={() => handleRatingsDialogOpen(false, RatingTypeEnum.NTRP)}
            >
              {ratingMessage}
            </RankingRatingLinkButton>,
          ]
        : []),
      ...(displayedScalingTypes.includes('PT')
        ? [
            <RankingRatingLinkButton
              key="PlayTracker"
              adornment={
                <Link href={getEnvConfig().REACT_APP_PLAYTRACKER_HELP_URL} target="_blank">
                  <IconButton disableFocusRipple disableRipple size="small" sx={{ padding: 0, marginRight: 1 }}>
                    <HelpIcon height={22} width={22} />
                  </IconButton>
                </Link>
              }
              label={'PlayTracker'}
              onClick={() => handleRatingsDialogOpen(false, RatingTypeEnum.PT)}
            >
              {ptMessage}
            </RankingRatingLinkButton>,
          ]
        : []),
      ...(displayedScalingTypes.includes('WTN')
        ? [
            <RankingRatingLinkButton
              key="WTN"
              label={'WTN'}
              onClick={() => handleRatingsDialogOpen(false, RatingTypeEnum.WTN)}
            >
              {wtnMessage}
            </RankingRatingLinkButton>,
          ]
        : []),
    ];

    return (
      <>
        {!!components.length && (
          <Grid container item alignItems="center" className={classes.ratingsContainer} gap={1}>
            {components.reduce((prev, next, i) => [prev, <Pipe key={`splitter${i}`} />, next])}
          </Grid>
        )}
        {isRankings ? (
          <RankingsDialog
            closeDialog={handleRankingsDialogClose}
            eventCategory={event?.category?.subType}
            open={rankingsOpen}
            publishedAt={{
              rankingPubDate: rankingTimeItem?.itemValue,
              mensRankingPubDate: mensRankingTimeItem?.itemValue,
              womensRankingPubDate: womensRankingTimeItem?.itemValue,
              ratingPubDate,
              ptPubDate: ptTimeItem?.itemValue,
            }}
            updatedAt={{
              rankingAccessDate,
              mensRankingAccessDate,
              womensRankingAccessDate,
              ratingAccessDate,
              ptAccessDate,
            }}
            useDualLists={showDoubleRankingsList}
          />
        ) : (
          <RatingsDialog
            closeDialog={handleRatingsDialogClose}
            open={ratingsOpen}
            publishedAt={{ ratingsDialogPubDate }}
            ratingType={ratingType}
            updatedAt={{ ratingsDialogAccessDate }}
          />
        )}
      </>
    );
  },
);

export default RankingsAndRatingsComponent;
